import { useState, useMemo, useCallback } from 'react';
import type { PlaylistItem } from '../types/PlaylistItem';
import type { PlaylistEngineState } from '../types/PlaylistEngineState';

interface UsePlaylistEngineReturn {
  state: PlaylistEngineState;
  setPlaylist: (newPlaylist: PlaylistItem[]) => void;
  next: () => void;
  previous: () => void;
  goTo: (index: number) => void;
  reset: () => void;
}

export function usePlaylistEngine(initialPlaylist: PlaylistItem[]): UsePlaylistEngineReturn {
  const validItems = useMemo(() => {
    return initialPlaylist
      .filter((item) => {
        if (item.is_active === false) return false;
        if (!item.media_url) return false;
        if (!item.type) return false;
        return true;
      })
      .sort((a, b) => {
        const orderA = a.sort_order;
        const orderB = b.sort_order;
        if (orderA !== undefined && orderB !== undefined) {
          return orderA - orderB;
        }
        if (orderA !== undefined) return -1;
        if (orderB !== undefined) return 1;
        return 0;
      });
  }, [initialPlaylist]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const clampedIndex = validItems.length > 0
    ? Math.min(currentIndex, validItems.length - 1)
    : 0;

  const next = useCallback(() => {
    if (validItems.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % validItems.length);
  }, [validItems.length]);

  const previous = useCallback(() => {
    if (validItems.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + validItems.length) % validItems.length);
  }, [validItems.length]);

  const goTo = useCallback(
    (index: number) => {
      if (index >= 0 && index < validItems.length) {
        setCurrentIndex(index);
      }
    },
    [validItems.length],
  );

  const reset = useCallback(() => {
    setCurrentIndex(0);
  }, []);

  const setPlaylist = useCallback(
    (_newPlaylist: PlaylistItem[]) => {
      setCurrentIndex(0);
    },
    [],
  );

  const state: PlaylistEngineState = {
    playlist: validItems,
    currentIndex: clampedIndex,
    currentItem: validItems.length > 0 ? validItems[clampedIndex] : null,
    hasNext: validItems.length > 1,
    hasPrevious: validItems.length > 1,
    isEmpty: validItems.length === 0,
  };

  return { state, setPlaylist, next, previous, goTo, reset };
}